import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FaCheckCircle, FaCopy, FaKey, FaShieldAlt, FaUserCircle } from 'react-icons/fa';
import { Button } from 'components/ui/button';
import { Card } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Badge } from 'components/ui/badge';

function UserProfile() {
  const [userData, setUserData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const getToken = () => sessionStorage.getItem('shortToken') || localStorage.getItem('shortToken');

  const strength = useMemo(() => {
    const value = newPassword || '';
    let score = 0;
    if (value.length >= 8) score++;
    if (/[A-Z]/.test(value)) score++;
    if (/[0-9]/.test(value)) score++;
    if (/[^A-Za-z0-9]/.test(value)) score++;
    return score;
  }, [newPassword]);

  const strengthLabel = useMemo(() => {
    if (!newPassword) return 'Sin definir';
    if (strength <= 1) return 'Débil';
    if (strength <= 2) return 'Media';
    if (strength === 3) return 'Buena';
    return 'Fuerte';
  }, [newPassword, strength]);

  const strengthColor = useMemo(() => {
    if (!newPassword) return 'bg-slate-200';
    if (strength <= 1) return 'bg-rose-500';
    if (strength <= 2) return 'bg-amber-500';
    if (strength === 3) return 'bg-blue-500';
    return 'bg-emerald-500';
  }, [newPassword, strength]);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const token = getToken();
        if (!token) {
          throw new Error('No se encontró el token de autenticación');
        }
        const response = await axios.get('/api/usuarios/me', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserData(response.data);
        localStorage.setItem('isAdmin', response.data.es_admin);
        setError('');
      } catch (error) {
        setError('Error al obtener datos del usuario: ' + error.message);
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const copyToClipboard = async (text, label) => {
    try {
      if (!text) return;
      await navigator.clipboard.writeText(String(text));
      setSuccessMessage(`${label} copiado`);
      setTimeout(() => setSuccessMessage(''), 1800);
    } catch (copyError) {
      setError(`No se pudo copiar ${label.toLowerCase()}`);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setSuccessMessage('');

    if (newPassword.length < 8) {
      setPasswordError('La nueva contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    try {
      setSubmitting(true);
      const token = getToken();
      await axios.put('/api/usuarios/change-password',
        { newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessMessage('Contraseña cambiada correctamente');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
    } catch (error) {
      setPasswordError('Error al cambiar la contraseña: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>;
  }

  if (loading || !userData) {
    return <div className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">Cargando perfil...</div>;
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <Card
        className="mb-5 border-0 p-6"
        style={{
          background: 'linear-gradient(90deg, #0f172a 0%, #1e293b 55%, #1d4ed8 100%)',
          color: '#ffffff',
        }}
      >
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="flex items-center text-3xl font-bold" style={{ color: '#ffffff' }}>
              <FaUserCircle className="mr-3" style={{ color: '#bfdbfe' }} />
              Perfil de Usuario
            </h2>
            <p className="mt-1 text-sm" style={{ color: '#dbeafe' }}>
              Gestiona tus datos de acceso y credenciales técnicas.
            </p>
          </div>
          <Badge
            className="border"
            style={{ background: 'rgba(255,255,255,0.16)', color: '#ffffff', borderColor: 'rgba(255,255,255,0.35)' }}
          >
            <FaShieldAlt className="mr-2" />
            {userData.es_admin ? 'Administrador' : 'Usuario'}
          </Badge>
        </div>
      </Card>

      {successMessage ? (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          <FaCheckCircle className="mr-2 inline" />
          {successMessage}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card className="p-5">
          <h3 className="mb-4 text-lg font-semibold text-slate-900">Datos de cuenta</h3>
          <div className="space-y-3 text-sm">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nombre</p>
              <p className="font-semibold text-slate-900">{userData.nombre || '-'}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Email</p>
              <p className="font-semibold text-slate-900">{userData.email || '-'}</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Usuario Radicale</p>
                  <p className="font-semibold text-slate-900">{userData.radicale_username || '-'}</p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copyToClipboard(userData.radicale_username, 'Usuario Radicale')}
                >
                  <FaCopy className="mr-1" />
                  Copiar
                </Button>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">ID Calendario</p>
                  <p className="font-semibold text-slate-900">{userData.calendar_id || '-'}</p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => copyToClipboard(userData.calendar_id, 'ID de calendario')}
                >
                  <FaCopy className="mr-1" />
                  Copiar
                </Button>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="mb-4 flex items-center text-lg font-semibold text-slate-900">
            <FaKey className="mr-2 text-slate-700" />
            Cambiar Contraseña
          </h3>
          <form onSubmit={handleChangePassword} className="space-y-3">
            <div>
              <label htmlFor="newPassword" className="mb-1 block text-sm font-medium text-slate-700">Nueva contraseña</label>
              <Input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
              />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="mb-1 block text-sm font-medium text-slate-700">Confirmar contraseña</label>
              <Input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between text-xs">
                <span className="font-semibold text-slate-500">Fortaleza</span>
                <span className="font-semibold text-slate-700">{strengthLabel}</span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                <div
                  className={`h-full transition-all ${strengthColor}`}
                  style={{ width: `${Math.max(8, (strength / 4) * 100)}%` }}
                />
              </div>
            </div>

            {passwordError ? (
              <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{passwordError}</p>
            ) : null}

            <Button type="submit" variant="primary" disabled={submitting}>
              {submitting ? 'Guardando...' : 'Actualizar contraseña'}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}

export default UserProfile;
